import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import clsx from 'clsx';

import { Button } from './Button';
import { InlineNotification, type NotificationKind } from './InlineNotification';
import styles from './Toast.module.css';

export interface ToastOptions {
  title: ReactNode;
  description?: ReactNode;
  kind?: NotificationKind;
  /**
   * Auto-dismiss delay. 0 keeps the toast until dismissed. Errors default to
   * sticky: a failure the user never read is a failure they will hit again.
   */
  timeoutMs?: number;
  /** Single inline action, e.g. "Undo" or "View run". */
  actionLabel?: string;
  onAction?: () => void;
}

interface ToastRecord extends ToastOptions {
  id: string;
  kind: NotificationKind;
  timeoutMs: number;
}

export interface ToastApi {
  /** Queue a toast; returns its id so the caller can dismiss it early. */
  toast: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export interface ToastProviderProps {
  children: ReactNode;
  /** Oldest toasts beyond this are dropped so the stack never covers the app. */
  max?: number;
  placement?: 'bottom-right' | 'top-right' | 'bottom-center';
  /** Screen-reader name for the notification region. */
  label?: string;
  defaultTimeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 6000;

export function ToastProvider({
  children,
  max = 4,
  placement = 'bottom-right',
  label = 'Notifications',
  defaultTimeoutMs = DEFAULT_TIMEOUT_MS,
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<readonly ToastRecord[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((entry) => entry.id !== id));
  }, []);

  const dismissAll = useCallback(() => setToasts([]), []);

  const toast = useCallback(
    (options: ToastOptions) => {
      counter.current += 1;
      const id = `toast-${counter.current}`;
      const kind = options.kind ?? 'info';
      const record: ToastRecord = {
        ...options,
        id,
        kind,
        timeoutMs: options.timeoutMs ?? (kind === 'error' ? 0 : defaultTimeoutMs),
      };
      setToasts((current) => [...current, record].slice(-max));
      return id;
    },
    [defaultTimeoutMs, max],
  );

  const api = useMemo<ToastApi>(() => ({ toast, dismiss, dismissAll }), [toast, dismiss, dismissAll]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* The live region is mounted with the app, empty. Screen readers only
          announce insertions into a region that already existed. */}
      <div className={clsx(styles.region, styles[placement])} role="region" aria-label={label}>
        <ol className={styles.list} aria-live="polite" aria-atomic="false">
          {toasts.map((entry) => (
            <ToastItem key={entry.id} toast={entry} onDismiss={dismiss} />
          ))}
        </ol>
      </div>
    </ToastContext.Provider>
  );
}

interface ToastItemProps {
  toast: ToastRecord;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const { id, timeoutMs } = toast;

  useEffect(() => {
    if (timeoutMs <= 0) return;
    const handle = window.setTimeout(() => onDismiss(id), timeoutMs);
    return () => window.clearTimeout(handle);
  }, [id, timeoutMs, onDismiss]);

  return (
    <li className={styles.item}>
      <InlineNotification
        kind={toast.kind}
        title={toast.title}
        onClose={() => onDismiss(id)}
        className={styles.notification}
        actions={
          toast.actionLabel === undefined ? undefined : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                toast.onAction?.();
                onDismiss(id);
              }}
            >
              {toast.actionLabel}
            </Button>
          )
        }
      >
        {toast.description}
      </InlineNotification>
    </li>
  );
}

/** Throws outside a ToastProvider — a silently dropped toast is worse. */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (api === null) throw new Error('useToast must be used inside a <ToastProvider>');
  return api;
}
